import { useMemo, useState } from 'react'
import { GoSortAsc, GoSortDesc } from 'react-icons/go'
import { FiSearch } from 'react-icons/fi'
import { CheckIcon } from '@radix-ui/react-icons'
import TableButton from './TableButton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'components/UI/BaseDropdown/dropdown-base'
import { SortDirection } from 'components/DataExpansionComponents/Common/commonSortOptionsHelper'
import { cn } from 'components/UI/cn'

export interface SortOption<T extends string = string> {
  label: string
  value: T
  direction: SortDirection
}

interface TableButtonSortProps<T extends string = string> {
  options: SortOption<T>[]
  selectedValue?: Omit<SortOption<T>, 'label'>
  onSelect: (value: T, direction: SortDirection) => void
}

export default function TableButtonSort<T extends string = string>({
  options,
  selectedValue,
  onSelect,
}: TableButtonSortProps<T>) {
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const selectedOption = useMemo(
    () =>
      options.find(
        (o) =>
          o.value === selectedValue?.value &&
          o.direction === selectedValue?.direction
      ) ?? null,
    [options, selectedValue]
  )

  const filteredOptions = useMemo(() => {
    const result = search
      ? options.filter((o) =>
          o.label.toLowerCase().includes(search.toLowerCase())
        )
      : [...options]

    if (selectedOption) {
      const selectedIdx = result.findIndex(
        (o) =>
          o.value === selectedOption.value &&
          o.direction === selectedOption.direction
      )
      if (selectedIdx > 0) {
        const [selected] = result.splice(selectedIdx, 1)
        result.unshift(selected)
      }
    }

    return result
  }, [options, search, selectedOption])

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
    >
      <DropdownMenuTrigger asChild>
        <TableButton
          icon={selectedOption?.direction === 'asc' ? GoSortAsc : GoSortDesc}
          text={selectedOption?.label ?? 'Sorting'}
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-62 bg-white px-2">
        <DropdownMenuLabel className="bg-transparent p-0 mb-2 font-medium text-gray-500">
          Sort by:
        </DropdownMenuLabel>
        <div className="flex items-center gap-2 border border-gray-300 p-2 rounded">
          <FiSearch size={20} />
          <input
            type="text"
            placeholder="Search by column type"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full bg-transparent text-sm outline-none placeholder:text-gray-400"
          />
        </div>
        <DropdownMenuSeparator />
        <div className="max-h-60 overflow-y-auto">
          {filteredOptions.length === 0 && (
            <div className="p-2 text-sm text-gray-500">No options found</div>
          )}
          {filteredOptions.map((option) => {
            const isSelected =
              selectedValue?.value === option.value &&
              selectedValue?.direction === option.direction
            return (
              <div
                key={`${option.value}-${option.direction}`}
                onClick={() => {
                  onSelect(option.value, option.direction)
                  setOpen(false)
                }}
                className={cn(
                  'flex items-center justify-between rounded p-2 text-sm cursor-pointer hover:bg-sage-lt',
                  {
                    'bg-new-company-pistachio-3': isSelected,
                  }
                )}
              >
                <div className="flex items-center gap-2">
                  {option.direction === 'asc' ? (
                    <GoSortAsc size={16} />
                  ) : (
                    <GoSortDesc size={16} />
                  )}
                  <span>{option.label}</span>
                </div>
                {isSelected && <CheckIcon className="h-4 w-4" />}
              </div>
            )
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
